(ns practitest.frontend.batch-modal.main
  (:require
    [clojure.string :as str]
    [practitest.frontend.batch-modal.events :as batch-modal-events]
    [practitest.frontend.batch-modal.fields :as batch-modal-fields]
    [practitest.frontend.batch-modal.subs :as batch-modal-subs]
    [practitest.frontend.common-entities.metadata :refer [get-entity-name]]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.grid.subs :as grid.subs]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.components-provider :refer [application-provider]]
    [practitest.frontend.components2.modal-header :as modal-header]
    [re-frame.core :as re-frame]))

;; Shared shell for batch grid operations, switched via `mode` (Batch Edit
;; today; Batch Clone planned). A field stays unchecked until opted into the
;; batch, so a prefilled value alone is never submitted.

(defn- track! [action entity]
  (mixpanel/track "Batch Edit" {"Action" action "Entity" (mixpanel/entity-type->mixpanel entity)}))

(defn- title-text [mode]
  (case mode
    :edit (get-translation "batch-modal.title.edit" "Batch Edit")))

(defn- submit-button-text [mode entity-label]
  (format-translation
    (case mode
      :edit (get-translation "batch-modal.footer.update" "Update %{entity}"))
    {:entity entity-label}))

(defn- selection-count [entity selection state-key]
  (if (= selection :_all)
    @(re-frame/subscribe [::grid.subs/entities-total-count entity state-key])
    (count selection)))

(defn- selection-ids-info [entity selection state-key]
  (when (set? selection)
    (let [rows @(re-frame/subscribe [::grid.subs/data [entity state-key]])
          id->display-id (into {} (map (juxt :id :display_id)) rows)
          display-ids (keep id->display-id selection)
          truncated? (> (count selection) 5)]
      (when (seq display-ids)
        {:text (str (str/join ", " (take 5 display-ids))
                    (when truncated? "..."))
         :full-text (str/join ", " display-ids)}))))

(defn- selection-ids-span [ids-info]
  [tooltip/mux-tooltip
   {}
   (:full-text ids-info)
   (fn [props] [:span.selection-ids props (:text ids-info)])])

(defn- selection-summary [entity selection state-key]
  (let [entity-label (get-entity-name entity :plural-name)
        count (selection-count entity selection state-key)
        ids-info (selection-ids-info entity selection state-key)]
    [:div.selection-summary
     (if ids-info
       (let [[before after] (str/split
                               (format-translation
                                 (get-translation "batch-modal.selection-summary"
                                                  "%{count} %{entity} Selected - IDs %{ids}")
                                 {:count count :entity entity-label})
                               #"%\{ids\}" 2)]
         [:<>
          before
          [selection-ids-span ids-info]
          after])
       (format-translation
         (get-translation "batch-modal.selection-summary-no-ids"
                          "%{count} %{entity} Selected")
         {:count count :entity entity-label}))]))

(defn- header [mode entity selection state-key]
  [modal-header/modal-header
   {:icon [icons/mux-pencil]
    :title (title-text mode)
    :context (get-entity-name entity :plural-name)
    :subtitle (get-translation "batch-modal.subtitle"
                               "Batch Edit - Modify several fields in a single action.")
    :aside [selection-summary entity selection state-key]}])

(defn- edit-values-section [entity mode]
  (let [fields (batch-modal-fields/get-batch-fields entity mode)]
    [:div.fields-section
     [:div.header
      [:div.title (get-translation "batch-modal.section.edit-values.title" "Edit Field Values")]
      [:div.subtitle
       (get-translation "batch-modal.section.edit-values.subtitle"
                        "Select the values to update.")
       " "
       [:span.mandatory-label (get-translation "batch-modal.section.edit-values.subtitle-emphasis"
                                 "Make sure all mandatory fields are filled in.")]]]
     [:div.fields-grid
      (for [field fields]
        ^{:key (:id field)} [batch-modal-fields/field-row entity field fields])]]))

(defn- batch-options-section [entity]
  (let [entity-label (get-entity-name entity :plural-name)
        comment @(re-frame/subscribe [::batch-modal-subs/comment])
        disable-notifications? @(re-frame/subscribe [::batch-modal-subs/disable-notifications?])]
    [:div.fields-section
     [:div.header
      [:div.title (get-translation "batch-modal.section.batch-options.title" "Set Batch Options")]]
     [:div.body
      [:div.field-row
       [:label.field-label.add-comment-label
        (format-translation
          (get-translation "batch-modal.section.batch-options.comment-label"
                           "Add a comment to all updated %{entity}")
          {:entity entity-label})]
       [:input.m-input
        {:type "text"
         :value comment
         :placeholder (get-translation "batch-modal.section.batch-options.comment-placeholder"
                                       "Add a comment...")
         :on-change #(re-frame/dispatch [::batch-modal-events/set-comment (-> % .-target .-value)])
         :on-blur #(when (seq comment)
                     (track! "Comment Added" entity))}]]
      [:div.field-row.checkbox
       [:input.m-checkbox
        {:type "checkbox"
         :id "batch-modal-disable-notifications"
         :checked disable-notifications?
         :on-change #(do
                       (when-not disable-notifications?
                         (track! "Mail Notifications Disabled" entity))
                       (re-frame/dispatch [::batch-modal-events/toggle-disable-notifications]))}]
       [:label.m-label {:for "batch-modal-disable-notifications"}
        (get-translation "batch-modal.section.batch-options.disable-notifications"
                         "Disable Mail Notifications of this update")]]]]))

(defn- run-validation! [entity mode]
  (let [fields (batch-modal-fields/get-batch-fields entity mode)
        field-values @(re-frame/subscribe [::batch-modal-subs/field-values])
        common-values @(re-frame/subscribe [::batch-modal-subs/common-values])
        errors (batch-modal-fields/mandatory-errors fields field-values common-values)]
    (track! "Update Clicked" entity)
    (re-frame/dispatch [::batch-modal-events/set-field-errors errors])
    (if (seq errors)
      (do
        (track! "Update Blocked Mandatory" entity)
        (when-let [first-id (->> fields (map :id) (filter errors) first)]
          (batch-modal-fields/scroll-to-field! first-id)))
      (let [selection @(re-frame/subscribe [::batch-modal-subs/selection])
            current-view @(re-frame/subscribe [::batch-modal-subs/current-view])
            grid-filter @(re-frame/subscribe [::batch-modal-subs/filter])
            set-id @(re-frame/subscribe [::batch-modal-subs/set-id])
            comment @(re-frame/subscribe [::batch-modal-subs/comment])
            disable-notifications? @(re-frame/subscribe [::batch-modal-subs/disable-notifications?])
            preserve-tags? @(re-frame/subscribe [::batch-modal-subs/preserve-tags?])
            params (batch-modal-fields/build-submit-params
                     entity fields field-values common-values selection current-view grid-filter set-id
                     comment disable-notifications? preserve-tags?)]
        (re-frame/dispatch [::batch-modal-events/submit params])))))

(defn- footer [mode entity close!]
  (let [entity-label (get-entity-name entity :plural-name)
        checked-count @(re-frame/subscribe [::batch-modal-subs/checked-fields-count])
        comment @(re-frame/subscribe [::batch-modal-subs/comment])]
    [:div.footer
     [:span.footer-counter
      (format-translation
        (get-translation "batch-modal.footer.counter" "%{count} fields selected for update")
        {:count checked-count})]
     [:div.footer-actions
      [buttons/button
       {:text (get-translation "generic.button.cancel" "Cancel")
        :on-click close!}]
      [buttons/button
       {:primary? true
        :disabled? (and (zero? checked-count) (str/blank? comment))
        :text (submit-button-text mode entity-label)
        :on-click #(run-validation! entity mode)}]]]))

(defn batch-modal []
  (let [open? @(re-frame/subscribe [::batch-modal-subs/open?])
        mode @(re-frame/subscribe [::batch-modal-subs/mode])
        entity @(re-frame/subscribe [::batch-modal-subs/entity])
        selection @(re-frame/subscribe [::batch-modal-subs/selection])
        state-key @(re-frame/subscribe [::batch-modal-subs/state-key])
        close! #(do
                  (track! "Batch Edit Cancelled" entity)
                  (re-frame/dispatch [::batch-modal-events/close]))]
    [application-provider
     [modal/bootstrap-modal
      {:is-open? open?
       :on-request-close close!
       :close-button? true
       :centered? true
       :scrollable? true
       :class "batch-modal"
       :title-as "div"}
      [header mode entity selection state-key]
      [:<>
       [edit-values-section entity mode]
       (when-not (= entity :instance)
         [batch-options-section entity])]
      [footer mode entity close!]]]))
